/**
 * TransactionSendCopyInvoiceModal
 *
 * Thin wrapper around SingleEmailModal that provides transaction-specific
 * props (merge data, invoice attachment fetching, send-copy logging).
 *
 * The exported interface is unchanged so page-level imports keep working.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import { type EmailAttachment, SingleEmailModal } from '@/components/shared/single-email-modal';
import { useAdmDocumentDownloadAsBase64 } from '@/hooks/useAdmDocumentDownloadAsBase64';
import { useCompanyProductType } from '@/hooks/useCompanyProductType';
import { useLogCustomerEmail } from '@/hooks/useLogCustomerEmail';
import { useLogSendCopyInvoice } from '@/hooks/useLogSendCopyInvoice';
import { useTransactionDocumentDownload } from '@/hooks/useTransactionDocumentDownload';
import { fetchAdmDocumentsForTransaction } from '@/hooks/useTransactionDocuments';
import { fetchAttachmentsWithProgress } from '@/lib/fetch-attachments-utils';
import { trackError } from '@/lib/telemetry';
import { useAuth } from '@/providers/AuthProvider';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import { useMode } from '@/providers/ModeProvider';
import type { Transaction } from '@/types/transaction';

// ─── Props (re-exported for backward compatibility) ──────────────────────────

export interface TransactionSendCopyInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selected transactions — must all belong to the same customer. */
  transactions: Transaction[];
  onSuccess?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function TransactionSendCopyInvoiceModal({
  open,
  onOpenChange,
  transactions,
  onSuccess,
}: Readonly<TransactionSendCopyInvoiceModalProps>) {
  const firstTx = transactions[0];
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const { isDimensions } = useCompanyProductType();
  const { downloadAdmDocumentAsBase64 } = useAdmDocumentDownloadAsBase64();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();
  const { isDemo } = useMode();
  const logSendCopyInvoice = useLogSendCopyInvoice();
  const logCustomerEmail = useLogCustomerEmail();

  // Derive customer info from the first transaction
  const customerId = firstTx?.customerId?.toString() ?? '';
  const customerName = firstTx?.customerName ?? '';
  const accountNumber = firstTx?.accountNumber ?? '';

  // Fetch invoice PDFs for all selected transactions
  const handleFetchAttachments = useCallback(
    async (onProgress: (done: number, total: number) => void): Promise<EmailAttachment[]> => {
      if (isDimensions) {
        // Dimensions path: fetch documents from ADM for all transactions with externalId
        if (isDemo) return [];

        const txsWithExternalId = transactions.filter((t) => !!t.externalId);
        if (txsWithExternalId.length === 0) return [];

        if (!getAccessToken) {
          toast.error('Authentication not available — cannot fetch documents');
          return [];
        }

        const ctx = await getDocumentContext();
        if (!ctx) {
          toast.error('Document API not available — could not discover instance');
          return [];
        }

        // Fetch document metadata for each transaction (limit 10 per transaction)
        const allDocs: { id: string; fileName: string; fileType: string }[] = [];
        let metadataFailures = 0;
        for (const tx of txsWithExternalId) {
          try {
            const docs = await fetchAdmDocumentsForTransaction(ctx, getAccessToken, tx.externalId!, 10);
            for (const doc of docs) {
              allDocs.push({ id: doc.id, fileName: doc.fileName, fileType: doc.fileType });
            }
          } catch {
            metadataFailures++;
          }
        }

        if (metadataFailures > 0 && allDocs.length === 0) {
          toast.warning(`${metadataFailures} transaction(s) could not be queried for documents`);
          return [];
        }

        if (allDocs.length === 0) return [];

        return fetchAttachmentsWithProgress(
          allDocs,
          (doc) => downloadAdmDocumentAsBase64(doc.id, doc.fileName, doc.fileType),
          onProgress,
        );
      }

      // Financials path: existing behavior — only fetch for invoice-type transactions
      const invoiceTxs = transactions.filter((t) => t.transactionType === 'invoice');
      if (invoiceTxs.length === 0) return [];

      return fetchAttachmentsWithProgress(invoiceTxs, (t) => downloadDocumentAsBase64(t.id), onProgress);
    },
    [
      transactions,
      isDimensions,
      isDemo,
      downloadDocumentAsBase64,
      downloadAdmDocumentAsBase64,
      getDocumentContext,
      getAccessToken,
    ],
  );

  // Log the send-copy-invoice action before the MFE step
  const handleBeforeSend = useCallback(
    (payload: { toEmails: string[]; ccEmails: string[]; subject: string }) => {
      logSendCopyInvoice.mutate(
        {
          customerId: firstTx?.customerId,
          recipientEmail: payload.toEmails[0] ?? '',
          message: payload.subject,
          transactionIds: transactions.map((t) => t.id),
        },
        {
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error('Failed to save send-copy-invoice to history'));
            toast.error('Email was sent but failed to save to history. Please contact support if the issue persists.');
          },
        },
      );
    },
    [logSendCopyInvoice, firstTx, transactions],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logCustomerEmail.mutate(
        {
          sourceId: customerId,
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
        },
        {
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error('Failed to save customer email to history'));
            toast.error('Email was sent but failed to save to history. Please contact support if the issue persists.');
          },
        },
      );
    },
    [logCustomerEmail, customerId],
  );

  if (!firstTx) return null;

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="transaction"
      transactionIds={transactions.map((t) => t.id)}
      callbackEventName="creditcontrol:send-copy-invoice-done"
      attachLabel="Attach invoice copies"
      fetchAttachments={handleFetchAttachments}
      onBeforeSend={handleBeforeSend}
      onAfterSend={handleAfterSend}
      successMessage={`Invoice copy email sent for ${transactions.length} transaction(s)`}
      onSuccess={onSuccess}
      allowedCategoryTypes={['transactions', 'general']}
      allowedTemplateTypes={['email']}
    />
  );
}
